import { useEffect, useRef } from 'react';
import { animate, createScope, createTimeline, stagger } from 'animejs';

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const revealOnScroll = (sections) => {
  if (prefersReducedMotion()) return () => {};

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const config = sections.get(entry.target);
        if (config?.targets?.length) {
          animate(config.targets, {
            translateY: [28, 0],
            scale: [0.97, 1],
            duration: 750,
            ease: 'out(4)',
            delay: stagger(90),
            ...config.options,
          });
        }

        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -30px 0px' },
  );

  sections.forEach((_, sectionEl) => {
    if (sectionEl instanceof Element) observer.observe(sectionEl);
  });

  return () => observer.disconnect();
};

export const useLandingAnimations = (mapStats) => {
  const rootRef = useRef(null);
  const scopeRef = useRef(null);
  const statsAnimatedRef = useRef(false);
  const statElsRef = useRef({ buildings: null, clients: null, cities: null });

  const setStatRef = (key) => (el) => {
    statElsRef.current[key] = el;
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return undefined;

    try {
      scopeRef.current = createScope({ root }).add(() => {
        createTimeline({ defaults: { ease: 'out(3)' } })
          .add('[data-hero-nav]', { translateY: [-14, 0], duration: 600 }, 0)
          .add('[data-hero-badge]', { scale: [0.9, 1], duration: 550 }, 80)
          .add('[data-hero-title]', { translateY: [28, 0], duration: 800 }, 160)
          .add('[data-hero-desc]', { translateY: [20, 0], duration: 700 }, 300)
          .add('[data-hero-cta] > *', {
            translateY: [18, 0],
            delay: stagger(80),
            duration: 600,
          }, 420)
          .add('[data-hero-trust] > *', {
            translateX: [-12, 0],
            delay: stagger(60),
            duration: 500,
          }, 560);

        animate('[data-orb]', {
          translateY: [-10, 10],
          duration: () => 3000 + Math.random() * 2000,
          alternate: true,
          loop: true,
          ease: 'inOut(2)',
          delay: stagger(300),
        });
      });
    } catch {
      // Si falla anime.js, el contenido sigue visible sin animación
    }

    const scrollSections = new Map();

    const featuresSection = root.querySelector('[data-features]');
    if (featuresSection) {
      scrollSections.set(featuresSection, {
        targets: featuresSection.querySelectorAll('[data-feature-card]'),
      });
    }

    const mapSection = root.querySelector('[data-map-section]');
    if (mapSection) {
      scrollSections.set(mapSection, {
        targets: [
          ...mapSection.querySelectorAll('[data-reveal]'),
          mapSection.querySelector('[data-map-visual]'),
        ].filter(Boolean),
        options: { translateX: [-16, 0] },
      });
    }

    const plansSection = root.querySelector('[data-plans-section]');
    if (plansSection) {
      scrollSections.set(plansSection, {
        targets: plansSection.querySelectorAll('[data-plan-card]'),
        options: { translateY: [24, 0] },
      });
    }

    const stepsSection = root.querySelector('[data-steps-section]');
    if (stepsSection) {
      scrollSections.set(stepsSection, {
        targets: [
          ...stepsSection.querySelectorAll('[data-step-item]'),
          stepsSection.querySelector('[data-arch-card]'),
        ].filter(Boolean),
      });
    }

    const ctaSection = root.querySelector('[data-cta-section]');
    if (ctaSection) {
      scrollSections.set(ctaSection, {
        targets: ctaSection.querySelectorAll('[data-reveal]'),
      });
    }

    const disconnectScroll = revealOnScroll(scrollSections);

    return () => {
      disconnectScroll();
      scopeRef.current?.revert();
    };
  }, []);

  useEffect(() => {
    const counters = { buildings: 0, clients: 0, cities: 0 };

    const animation = animate(counters, {
      buildings: mapStats.buildings,
      clients: mapStats.clients,
      cities: mapStats.cities,
      duration: statsAnimatedRef.current ? 600 : 1200,
      ease: 'out(4)',
      onUpdate: () => {
        Object.keys(counters).forEach((key) => {
          const el = statElsRef.current[key];
          if (el) el.textContent = Math.round(counters[key]);
        });
      },
    });

    statsAnimatedRef.current = true;
    return () => animation.revert();
  }, [mapStats.buildings, mapStats.clients, mapStats.cities]);

  return { rootRef, setStatRef };
};
